import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, ClipboardList, FileText, BarChart3, ChevronDown, MapPin, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { CTASection } from '../../components/landing/CTASection';

export default function TimesheetSoftware() {
  useEffect(() => {
    document.title = 'Timesheet Software | Track Employee Hours | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Track employee work hours, manage timesheets, and prepare payroll with ReStaff.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="timesheet-software-page">
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
            Timesheet Software
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Timesheet software is a core feature of workforce management systems used to track employee work hours, calculate totals, and prepare payroll.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="timesheet-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
            What is Timesheet Software?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Timesheet software automatically records employee work hours from clock-in and clock-out events, calculates regular and overtime totals, and generates payroll-ready reports. It eliminates manual hour tracking, reduces payroll errors, and gives managers a clear view of who worked when and for how long.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Employees Clock In & Out', desc: 'Staff clock in and out using the kiosk, phone, or desktop. Every punch is recorded automatically with the exact time.', icon: Clock },
              { step: '2', title: 'Hours Are Calculated', desc: 'ReStaff calculates total hours, breaks, and overtime for each employee. Late arrivals and missed punches are flagged for review.', icon: ClipboardList },
              { step: '3', title: 'Export for Payroll', desc: 'Managers review and approve timesheets, then export payroll-ready reports by employee, location, or date range.', icon: FileText },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="bg-white border border-slate-100 rounded-xl p-6 text-center" data-testid={`timesheet-step-${s.step}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Timesheet Features
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {[
              { title: 'Automatic Hour Calculation', desc: 'Total hours are calculated automatically from clock events. No manual math, no spreadsheet formulas — just accurate totals every time.', icon: Clock },
              { title: 'Overtime Tracking', desc: 'Overtime hours are identified and separated automatically. Set your overtime rules and ReStaff handles the rest.', icon: ShieldCheck },
              { title: 'Payroll-Ready Reports', desc: 'Export timesheets as CSV for your payroll provider. Filter by employee, date range, or location for clean, auditable records.', icon: FileText },
              { title: 'Multi-Location Timesheets', desc: 'Each location generates its own timesheets. Managers view location-specific data while owners see the full picture.', icon: MapPin },
              { title: 'Approval Workflow', desc: 'Managers review timesheets before export. Flag discrepancies, adjust entries, and approve — keeping payroll accurate.', icon: ClipboardList },
              { title: 'Date Range Filtering', desc: 'View timesheets for any period — this week, last week, custom range. Aggregate hours by employee or by day.', icon: BarChart3 },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`timesheet-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Why Use ReStaff for Timesheets
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Eliminate manual hour tracking and calculation errors',
              'Save hours of admin time on payroll preparation',
              'Improve payroll accuracy with automated timesheets',
              'Get full visibility into hours worked across every location',
              'Catch overtime and discrepancies before payroll runs',
              'Maintain auditable records for compliance',
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            Timesheets work best with ReStaff <Link to="/time-tracking" className="text-orange-500 hover:underline font-medium">time tracking software</Link>, <Link to="/employee-scheduling" className="text-orange-500 hover:underline font-medium">employee scheduling</Link>, <Link to="/labor-cost-management" className="text-orange-500 hover:underline font-medium">labor cost management</Link>, and <Link to="/task-management" className="text-orange-500 hover:underline font-medium">task management software</Link>. Used by <Link to="/clinics" className="text-orange-500 hover:underline font-medium">medical clinics</Link>, <Link to="/salons" className="text-orange-500 hover:underline font-medium">salons</Link>, and more.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'What is timesheet software?', a: 'Timesheet software records employee work hours automatically from clock events, calculates totals and overtime, and generates reports ready for payroll processing. It replaces manual tracking with an accurate, digital system.' },
              { q: 'How do timesheets work in ReStaff?', a: 'When employees clock in and out, ReStaff records the time automatically. Hours are calculated, overtime is flagged, and managers can review, adjust, and approve timesheets before exporting them for payroll.' },
              { q: 'How do I prepare payroll using timesheets?', a: 'In ReStaff, select the date range and location, review the calculated hours and overtime for each employee, approve the entries, then export a CSV file for your payroll provider.' },
              { q: 'Can I track timesheets for multiple locations?', a: 'Yes. Each location has its own timesheets. Managers see data for their assigned branches while owners can view and export timesheets across all locations.' },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`timesheet-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                  {item.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
